import { getEarliestDateTime, isDefined, parseDateAsLocal } from '@/composables/utils'
import { StoredProcedure } from '@/enums/StoredProcedure'
import { StoreId } from '@/enums/StoreId'
import { TableStoreId } from '@/enums/TableStoreId'
import type { ICustomFieldsStore } from '@/interfaces/api/ICustomFieldsStore'
import type { IStatistics } from '@/interfaces/IStatistics'
import type { IUsage } from '@/interfaces/IUsage'
import { useAPIStore } from '@/stores/db/ApiStore'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import Colors from '@/styles/_colors.module.scss'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { TableStoreFactory } from '../db/TableStoreFactory'

export const useDashboardStore = defineStore(
  StoreId.Dashboard,
  () => {
    // STORES, IMPORTS, & COMPOSABLES
    const apiStore = useAPIStore()
    const customFieldsStore = TableStoreFactory.get(TableStoreId.CustomFields) as ICustomFieldsStore
    const i18n = useI18n({ useScope: 'global' })
    const settingsStore = useSettingsStore()

    // REACTIVE VARIABLES
    const filters = ref<Record<string, Record<string, boolean>>>({})
    const monthlyUsage = ref<IUsage>({
      categories: [],
      series: []
    })
    const stats = ref<{ testing: IStatistics; training: IStatistics; clearance: IStatistics }>({
      testing: {
        compliant: 0,
        dueSoonScheduled: 0,
        dueSoonNotScheduled: 0,
        overdueScheduled: 0,
        overdueNotScheduled: 0,
        noRecord: 0,
        total: 0,
        compliantPercent: 0,
        dueSoonScheduledPercent: 0,
        dueSoonNotScheduledPercent: 0,
        overdueScheduledPercent: 0,
        overdueNotScheduledPercent: 0,
        noRecordPercent: 0,
        series: [
          {
            name: i18n.t('labels.percentage'),
            data: []
          }
        ]
      },
      training: {
        compliant: 0,
        dueSoonScheduled: 0,
        dueSoonNotScheduled: 0,
        overdueScheduled: 0,
        overdueNotScheduled: 0,
        noRecord: 0,
        total: 0,
        compliantPercent: 0,
        dueSoonScheduledPercent: 0,
        dueSoonNotScheduledPercent: 0,
        overdueScheduledPercent: 0,
        overdueNotScheduledPercent: 0,
        noRecordPercent: 0,
        series: [
          {
            name: i18n.t('labels.percentage'),
            data: []
          }
        ]
      },
      clearance: {
        compliant: 0,
        dueSoonScheduled: 0,
        dueSoonNotScheduled: 0,
        overdueScheduled: 0,
        overdueNotScheduled: 0,
        noRecord: 0,
        total: 0,
        compliantPercent: 0,
        dueSoonScheduledPercent: 0,
        dueSoonNotScheduledPercent: 0,
        overdueScheduledPercent: 0,
        overdueNotScheduledPercent: 0,
        noRecordPercent: 0,
        series: [
          {
            name: i18n.t('labels.percentage'),
            data: []
          }
        ]
      }
    })

    // COMPUTED PROPERTIES

    // FUNCTIONS - PRIVATE
    const _initFilters = () => {
      const newFilter: Record<string, Record<string, boolean>> = {}
      customFieldsStore.activeFieldDefinitions.forEach((definition) => {
        newFilter[definition.valueField] = {}
        const oldFilter = filters.value[definition.valueField]
        if (definition.item?.combo === 0) {
          definition.selectOptions.forEach((option) => {
            if (option.value) {
              if (oldFilter && isDefined(oldFilter[option.value])) {
                newFilter[definition.valueField][option.value] =
                  filters.value[definition.valueField][option.value]
              } else {
                newFilter[definition.valueField][option.value] = false
              }
            }
          })
        } else if (definition.item?.autoComplete) {
          definition.autoValues.forEach((value) => {
            if (value) {
              if (oldFilter && isDefined(oldFilter[value])) {
                newFilter[definition.valueField][value] =
                  filters.value[definition.valueField][value]
              } else {
                newFilter[definition.valueField][value] = false
              }
            }
          })
        }
      })
      filters.value = newFilter
    }

    // FUNCTIONS - PUBLIC
    const convertToString = (filter: Record<string, boolean>) => {
      if (!filter) {
        return ''
      }
      const items: Array<string> = []
      const keys = Object.keys(filter)
      keys.forEach((key: string) => {
        if (filter[key] === true) {
          items.push(key)
        }
      })

      if (items.length === 0) {
        return ''
      }
      return items.join(',')
    }

    const createSeries = (
      stats: IStatistics,
      expires: boolean = true,
      msgCompliant?: string,
      msgNoRecord?: string
    ) => {
      const data = []
      if (stats.compliant > 0) {
        data.push({
          name: msgCompliant ?? i18n.t('charts.statistics.compliant'),
          y: stats.compliantPercent,
          color: Colors.chartCompliant,
          raw: stats.compliant
        })
      }
      if (stats.dueSoonScheduled > 0 && expires) {
        data.push({
          name: i18n.t('charts.statistics.dueSoonScheduled'),
          y: stats.dueSoonScheduledPercent,
          color: Colors.chartDueSoonScheduled,
          raw: stats.dueSoonScheduled
        })
      }
      if (stats.dueSoonNotScheduled > 0 && expires) {
        data.push({
          name: i18n.t('charts.statistics.dueSoonNotScheduled'),
          y: stats.dueSoonNotScheduledPercent,
          color: Colors.chartDueSoonNotScheduled,
          raw: stats.dueSoonNotScheduled
        })
      }
      if (stats.overdueScheduled > 0 && expires) {
        data.push({
          name: i18n.t('charts.statistics.overdueScheduled'),
          y: stats.overdueScheduledPercent,
          color: Colors.chartOverdueScheduled,
          raw: stats.overdueScheduled
        })
      }
      if (stats.overdueNotScheduled > 0 && expires) {
        data.push({
          name: i18n.t('charts.statistics.overdueNotScheduled'),
          y: stats.overdueNotScheduledPercent,
          color: Colors.chartOverdueNotScheduled,
          raw: stats.overdueNotScheduled
        })
      }
      if (stats.noRecord > 0) {
        data.push({
          name: msgNoRecord ?? i18n.t('charts.statistics.noRecord'),
          y: stats.noRecordPercent,
          color: Colors.chartNoRecord,
          raw: stats.noRecord
        })
      }
      stats.series = [
        {
          name: i18n.t('labels.percentage'),
          data
        }
      ]
    }

    const getCustomFieldParams = (includeAll: boolean = true) => {
      const results = []
      if (includeAll) {
        results.push(settingsStore.globals.testing.daysUntilDue)
        results.push(settingsStore.globals.training.daysUntilDue)
        results.push(settingsStore.globals.clearance.daysUntilDue)
      }

      results.push(convertToString(filters.value.company))
      results.push(convertToString(filters.value.location))
      results.push(convertToString(filters.value.custom1Data))
      results.push(convertToString(filters.value.custom2Data))
      results.push(convertToString(filters.value.custom3Data))
      results.push(convertToString(filters.value.custom4Data))

      if (includeAll) {
        const today = getEarliestDateTime().toISOString()
        results.push(today)
      }
      return results
    }

    const loadStatistics = async () => {
      _initFilters()
      const response = await apiStore.execute(StoredProcedure.GetStatistics, getCustomFieldParams())
      if (!response.error && response.data) {
        const results = response.data.results[0]

        const trainingExpires = settingsStore.globals.training.expires
        const clearanceExpires = settingsStore.globals.clearance.expires

        let trainingCompliant = results.trainingCompliant
        if (!trainingExpires) {
          trainingCompliant +=
            results.trainingDueSoonScheduled +
            results.trainingDueSoonNotScheduled +
            results.trainingOverdueScheduled +
            results.trainingOverdueNotScheduled
        }

        let clearanceCompliant = results.clearanceCompliant
        if (!clearanceExpires) {
          clearanceCompliant +=
            results.clearanceDueSoonScheduled +
            results.clearanceDueSoonNotScheduled +
            results.clearanceOverdueScheduled +
            results.clearanceOverdueNotScheduled
        }

        stats.value = {
          testing: {
            compliant: results.testCompliant,
            dueSoonScheduled: results.testDueSoonScheduled,
            dueSoonNotScheduled: results.testDueSoonNotScheduled,
            overdueScheduled: results.testOverdueScheduled,
            overdueNotScheduled: results.testOverdueNotScheduled,
            noRecord: results.testNoRecord,
            total: results.total,
            compliantPercent: results.testCompliant / results.total,
            dueSoonScheduledPercent: results.testDueSoonScheduled / results.total,
            dueSoonNotScheduledPercent: results.testDueSoonNotScheduled / results.total,
            overdueScheduledPercent: results.testOverdueScheduled / results.total,
            overdueNotScheduledPercent: results.testOverdueNotScheduled / results.total,
            noRecordPercent: results.testNoRecord / results.total,
            series: []
          },
          training: {
            compliant: trainingCompliant,
            dueSoonScheduled: results.trainingDueSoonScheduled,
            dueSoonNotScheduled: results.trainingDueSoonNotScheduled,
            overdueScheduled: results.trainingOverdueScheduled,
            overdueNotScheduled: results.trainingOverdueNotScheduled,
            noRecord: results.trainingNoRecord,
            total: results.total,
            compliantPercent: trainingCompliant / results.total,
            dueSoonScheduledPercent: results.trainingDueSoonScheduled / results.total,
            dueSoonNotScheduledPercent: results.trainingDueSoonNotScheduled / results.total,
            overdueScheduledPercent: results.trainingOverdueScheduled / results.total,
            overdueNotScheduledPercent: results.trainingOverdueNotScheduled / results.total,
            noRecordPercent: results.trainingNoRecord / results.total,
            series: []
          },
          clearance: {
            compliant: clearanceCompliant,
            dueSoonScheduled: results.clearanceDueSoonScheduled,
            dueSoonNotScheduled: results.clearanceDueSoonNotScheduled,
            overdueScheduled: results.clearanceOverdueScheduled,
            overdueNotScheduled: results.clearanceOverdueNotScheduled,
            noRecord: results.clearanceNoRecord,
            total: results.total,
            compliantPercent: clearanceCompliant / results.total,
            dueSoonScheduledPercent: results.clearanceDueSoonScheduled / results.total,
            dueSoonNotScheduledPercent: results.clearanceDueSoonNotScheduled / results.total,
            overdueScheduledPercent: results.clearanceOverdueScheduled / results.total,
            overdueNotScheduledPercent: results.clearanceOverdueNotScheduled / results.total,
            noRecordPercent: results.clearanceNoRecord / results.total,
            series: []
          }
        }
        createSeries(stats.value.testing)
        createSeries(stats.value.training, settingsStore.globals.training.expires)
        createSeries(
          stats.value.clearance,
          settingsStore.globals.clearance.expires,
          i18n.t('labels.cleared'),
          i18n.t('labels.notCleared')
        )
      }
    }

    const loadMonthlyUsage = async () => {
      const response = await apiStore.execute(
        StoredProcedure.GetMonthlyUsage,
        getCustomFieldParams(false)
      )
      if (!response.error && response.data) {
        const results = response.data.results

        // Gets all categories from the results.
        const categoryMap: Map<string, string> = new Map()

        // Map the months to their labels.
        results.forEach((result: Record<string, any>) => {
          const date = parseDateAsLocal(result.month)
          const label = `${date.toLocaleString(settingsStore.localeCode, { month: 'short' })} ${date.getFullYear()}`
          categoryMap.set(result.month, label)
        })

        // Default series data. Currently only quantitative and qualitative are supported.
        const quantitativeData: { name: string; data: Array<number> } = {
          name: i18n.t('labels.usage.quantitative'),
          data: []
        }
        const qualitativeData: { name: string; data: Array<number> } = {
          name: i18n.t('labels.usage.qualitative'),
          data: []
        }

        const months = Array.from(categoryMap.keys())
        for (const month of months) {
          const qualitativeRecords = results.filter((result: Record<string, any>) => {
            return result.month === month && result.algorithm === 2
          })
          const quantitativeRecords = results.filter((result: Record<string, any>) => {
            return result.month === month && result.algorithm !== 2
          })
          const qualitativeTotal = qualitativeRecords.reduce(
            (acc: number, record: Record<string, any>) => {
              return acc + record.total
            },
            0
          )
          const quantitativeTotal = quantitativeRecords.reduce(
            (acc: number, record: Record<string, any>) => {
              return acc + record.total
            },
            0
          )
          qualitativeData.data.push(qualitativeTotal)
          quantitativeData.data.push(quantitativeTotal)
        }

        monthlyUsage.value = {
          categories: Array.from(categoryMap.values()),
          series: [quantitativeData, qualitativeData]
        }
      }
    }

    return {
      loadMonthlyUsage,
      loadStatistics,
      monthlyUsage,
      filters,
      stats
    }
  },
  {
    persist: {
      storage: localStorage,
      pick: ['filters']
    }
  }
)
